// src/services/studioApi.ts
// A single, small HTTP client shared by every Studios & Community service.
//
// Deliberately different from the older per-service `makeRequest` helpers:
//   - one base URL (config.apiBaseUrl), never hand-rebuilt
//   - one error type, always carrying a message safe to show a user
//   - a 401 never clears tokens or redirects; the caller decides what that means
//   - the ApiResponse envelope is unwrapped here, so pages work with plain data

import { config } from '../config';
import unifiedAuth from '../utils/unifiedAuth';

/** The envelope every Studios & Community endpoint returns. */
interface ApiEnvelope<T> {
  success: boolean;
  message?: string;
  data?: T;
  error?: string;
}

export class StudioApiError extends Error {
  readonly status: number;
  readonly code?: string;

  constructor(message: string, status: number, code?: string) {
    super(message);
    this.name = 'StudioApiError';
    this.status = status;
    this.code = code;
  }

  get isUnauthenticated(): boolean {
    return this.status === 401 || this.status === 403;
  }

  get isNotFound(): boolean {
    return this.status === 404;
  }

  get isConflict(): boolean {
    return this.status === 409;
  }
}

type QueryValue = string | number | boolean | null | undefined;

export function buildQuery(params: Record<string, QueryValue>): string {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === null || value === undefined || value === '') return;
    search.append(key, String(value));
  });
  const query = search.toString();
  return query ? `?${query}` : '';
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  /** Send the bearer token if we have one, but don't require it. */
  withAuth?: boolean;
  signal?: AbortSignal;
}

async function readErrorMessage(response: Response): Promise<{ message: string; code?: string }> {
  try {
    const payload = await response.json();

    // Field-level validation errors (400s from @Valid on the request DTOs) carry a
    // generic top-level message ("One or more fields have validation errors") plus a
    // `fieldErrors` array with the actual per-field reasons - prefer those when present
    // so a form like the casting application says *why*, not just that something failed.
    if (Array.isArray(payload?.fieldErrors) && payload.fieldErrors.length > 0) {
      const fieldMessages = payload.fieldErrors
        .map((fe: { message?: string }) => fe?.message)
        .filter((message: unknown): message is string => Boolean(message));
      if (fieldMessages.length > 0) {
        return { message: fieldMessages.join('. '), code: payload?.errorCode };
      }
    }

    const message =
      payload?.message || payload?.error || payload?.detail || `Request failed (${response.status})`;
    return { message, code: payload?.errorCode };
  } catch {
    return { message: `Request failed (${response.status})` };
  }
}

export async function studioRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, withAuth = true, signal } = options;

  const headers: Record<string, string> = { Accept: 'application/json' };
  if (body !== undefined) {
    headers['Content-Type'] = 'application/json';
  }

  // `withAuth: false` still means what it says. Several public endpoints pass it
  // deliberately - community stats, featured productions, open casting calls - and
  // routing those through unifiedAuth.fetch would attach a token to a request whose
  // caller explicitly asked for none. Authenticated calls get the shared path, and
  // with it refresh-then-retry; unauthenticated ones get a plain fetch.
  const send = withAuth ? unifiedAuth.fetch : fetch;

  let response: Response;
  try {
    response = await send(`${config.apiBaseUrl}${path}`, {
      method,
      headers,
      signal,
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error;
    throw new StudioApiError('Could not reach StrymHub. Check your connection and try again.', 0);
  }

  if (!response.ok) {
    const { message, code } = await readErrorMessage(response);
    throw new StudioApiError(message, response.status, code);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  const envelope = (await response.json()) as ApiEnvelope<T> | T;

  if (envelope && typeof envelope === 'object' && 'success' in envelope) {
    const wrapped = envelope as ApiEnvelope<T>;
    if (!wrapped.success) {
      throw new StudioApiError(wrapped.message || wrapped.error || 'Request failed', response.status);
    }
    return wrapped.data as T;
  }

  return envelope as T;
}

/** Turns any thrown value into a message that is safe to render. */
export function toDisplayMessage(error: unknown, fallback = 'Something went wrong.'): string {
  if (error instanceof StudioApiError) return error.message;
  if (error instanceof Error && error.message) return error.message;
  return fallback;
}
